import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Localization } from "../types/types";
import { useLocalization } from "./useLocalization";
import {
  localizationToPrefix,
  splitLocalePath,
  swapLocalePrefix,
  withLocalePrefix,
} from "../utils/localePaths";

export const useLocalePath = () => {
  const { locale: language } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();

  const prefix = localizationToPrefix(language);
  const internalPath = useMemo(() => splitLocalePath(location.pathname).path, [location.pathname]);

  const localized = useCallback(
    (path: string, targetLanguage: Localization = language) =>
      withLocalePrefix(path, localizationToPrefix(targetLanguage)),
    [language],
  );

  const goTo = useCallback(
    (path: string) => {
      void navigate(localized(path));
    },
    [localized, navigate],
  );

  const changeLanguagePath = useCallback(
    (nextLanguage: Localization) => {
      const nextPath = swapLocalePrefix(location.pathname, localizationToPrefix(nextLanguage));
      void navigate(`${nextPath}${location.search}${location.hash}`);
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  const isActive = useCallback(
    (path: string) => {
      if (path === "/") return internalPath === "/";
      return internalPath === path || internalPath.startsWith(`${path}/`);
    },
    [internalPath],
  );

  return {
    language,
    prefix,
    internalPath,
    localized,
    goTo,
    changeLanguagePath,
    isActive,
  };
};
